import { Box } from '@mui/material'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import List from 'components/core/list'
import { C1TableRedux, C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import { DisplayUserNames } from 'components/core/search/users'
import { useErrorReporting } from 'components/no-ui/error-reporting/datadog'
import { AppDisplayName } from 'components/pages/app/app-display-name'
import { C1EntitlementDisplayName } from 'components/pages/entitlement/display-name'
import { QUERIES } from 'components/pages/explore/query/Query'
import { AppEntitlementValueWithDeactivatedOwner } from 'lib/actions/apps/app-entitlement'
import { SearchAppEntitlementsWithDeactivatedOwnerRequest } from 'pbts/c1/api/app/v1/app_entitlement'
import { ExportDataRequest, QueryId } from 'pbts/c1/api/exportdata/v1/exportdata'
import { FC, useCallback, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { searchEntitlementsWithDeactivatedOwner } from 'redux_toolkit/entitlements'
import { ExportData } from './ExportData'
import { StructuredSearchStyles } from './styles'

const cols: Array<C1TableReduxColumn<AppEntitlementValueWithDeactivatedOwner>> = [
    {
        field: 'name',
        label: <FormattedMessage id="components_entitlement_with_deactivated_owner_entitlement_name" defaultMessage="Entitlement" />,
        Component: ({ row }) => <Box sx={{ overflow: 'hidden' }}>
            <C1EntitlementDisplayName entitlement={row.entitlement} />
        </Box>,
        maxWidth: '300px',
    },
    {
        field: 'app',
        label: <FormattedMessage id="components_entitlement_with_deactivated_owner_list_app" defaultMessage="App" />,
        Component: ({ row }) => <Box>
            {row.entitlement?.app ? (
                <AppDisplayName app={row.entitlement.app} avatarSize="xs" />
            ) : (
                <C1EmptyIndicator />
            )}
        </Box>,
    },
    {
        field: 'owner',
        label: <FormattedMessage id="components_entitlement_with_deactivated_owner_list_owner" defaultMessage="Entitlement owner" />,
        Component: ({ row }) => <Box>
            <DisplayUserNames
                expandable
                proposedOptions={row.entitlementOwners}
            />
        </Box>,
    },
]

const exportDataActionRequest = ExportDataRequest.create({
    queryId: QueryId.APP_ENTITLEMENTS_WITH_DEACTIVATED_OWNER,
    request: {
        oneofKind: 'searchAppEntitlementsWithDeactivatedOwnerRequest',
        searchAppEntitlementsWithDeactivatedOwnerRequest:
            SearchAppEntitlementsWithDeactivatedOwnerRequest.create({ expandMask: { paths: ['*'] } }),
    },
})

export const AppEntitlementsWithDeactivatedOwnerReport: FC = () => {
    const [page, setPage] = useState<number>(0)
    const { data,
        isError,
        isLoading,
        error,
        canLoadMore,
        getPage } = searchEntitlementsWithDeactivatedOwner.useSearchAppEntitlementsWithDeactivatedOwner(
        SearchAppEntitlementsWithDeactivatedOwnerRequest.create({ expandMask: { paths: ['*'] } }),
        {},
    )
    const onPageChange = useCallback(
        async (n: number) => {
            setPage(n)
            await getPage(n)
        },
        [getPage],
    )

    useErrorReporting(isError, error, QUERIES.AppEntitlementsWithDeactivatedOwner)

    const table = useMemo(
        () => (
            <C1TableRedux
                data={data}
                cols={cols}
                isError={isError}
                isLoading={isLoading}
                error={error}
                page={page}
                canLoadMore={canLoadMore}
                onPageChange={onPageChange}
            />
        ),
        [data, error, isError, isLoading, page, canLoadMore, onPageChange],
    )

    return <>
        <Box sx={StructuredSearchStyles.exportDataBox}>
            <ExportData exportDataRequest={exportDataActionRequest} />
        </Box>
        <List<AppEntitlementValueWithDeactivatedOwner> table={table} />
    </>
}
